import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { EditBenefitCalculationSubmitParams } from '@/features/serviceModal/components/EditBenefitCalculationModal/types';
import { OverrideBenefitCalculationSubmitParams } from '@/features/serviceModal/components/OverrideBenefitCalculationModal/types';
import { call, put, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { getBenefitSectionDetails } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import HttpService from '@/services/HttpService/HttpService';
import SnacksService from '@/services/SnackService/SnackService';
import {
  createBenefitCalculationDetails,
  createBenefitCalculationDetailsError,
  getBenefitCalculationDetails,
  getBenefitCalculationDetailsError,
  getBenefitCalculationDetailsSuccess,
  overrideBenefitCalculationFields,
  recalculateBenefitCalculationDetails,
  recalculateBenefitCalculationDetailsError,
  startLoadingBenefitCalculationDetails,
  updateBenefitCalculationField,
} from '../store/BenefitCalculationSlice';

function* getBenefitCalculationDetailsSaga(
  action: PayloadAction<{
    coverageId: string;
  }>,
) {
  try {
    if (!action.payload.coverageId) return;

    yield put(startLoadingBenefitCalculationDetails());

    const { data: benefitCalculationDetailsData } = yield call(
      HttpService.get,
      `examiner/benefit-calculation/${action.payload.coverageId}/claim-coverage`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    const { data: benefitCalculationHeaderData } = yield call(
      HttpService.get,
      `/v2/ClaimCoverage/${action.payload.coverageId}?includeEntities=Coverage,Claim.Policy`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(
      EdsAdapterService.normalize,
      benefitCalculationHeaderData,
      {
        section: EdsDefinitionSections.SidePanel,
      },
    );

    if (benefitCalculationDetailsData && normalizedResponse) {
      EdsDebugService.logResponse(EdsEntityCodes.BenefitCalculation, {
        rawResponse: benefitCalculationHeaderData,
        normalizedResponse,
        includedEntitiesRecord: normalizedResponse.entities,
      });

      yield put(
        getBenefitCalculationDetailsSuccess({
          benefitCalculationDetailsData,
          benefitCalculationHeaderData: normalizedResponse,
        }),
      );
    }
  } catch (error) {
    yield put(getBenefitCalculationDetailsError());

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to get the benefit calculation details, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    console.error(error);
  }
}

function* createBenefitCalculationDetailsSaga(
  action: PayloadAction<{
    coverageId: string;
  }>,
) {
  try {
    if (!action.payload.coverageId) return;

    yield put(startLoadingBenefitCalculationDetails());

    yield call(
      HttpService.post,
      `examiner/benefit-calculation/${action.payload.coverageId}/claim-coverage`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    yield* getBenefitCalculationDetailsSaga(action);
  } catch (error) {
    yield put(createBenefitCalculationDetailsError());

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to create a benefit calculation, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    console.error(error);
  }
}

function* recalculateBenefitCalculationDetailsSaga(
  action: PayloadAction<{
    coverageId: string;
    claimId: string;
  }>,
) {
  try {
    if (!action.payload.coverageId || !action.payload.claimId) return;

    yield put(startLoadingBenefitCalculationDetails());

    yield call(
      HttpService.post,
      `examiner/benefit-calculation/recalculate/claim-coverage/${action.payload.coverageId}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    yield put(getBenefitSectionDetails(action.payload.claimId));
    yield* getBenefitCalculationDetailsSaga(action);
  } catch (error) {
    yield put(recalculateBenefitCalculationDetailsError());

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to recalculate the updates, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    console.error(error);
  }
}

function* updateBenefitCalculationFieldSaga(action: PayloadAction<EditBenefitCalculationSubmitParams>) {
  try {
    const { updates, coverageId } = action.payload;

    if (!updates?.length || !coverageId) return;

    yield call(HttpService.put, `examiner/benefit-calculation/bulk-update-values`, action.payload.updates, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });

    yield* getBenefitCalculationDetailsSaga(action);

    SnacksService.add({
      type: SnackType.Success,
      title: `Updates saved successfully!`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the updates, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    console.error(error);
  }
}

function* overrideBenefitCalculationFieldsSaga(action: PayloadAction<OverrideBenefitCalculationSubmitParams>) {
  try {
    const { updates, coverageId, claimId } = action.payload;

    if (!updates?.length || !coverageId || !claimId) return;

    yield call(HttpService.put, `examiner/benefit-calculation/bulk-override`, action.payload.updates, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });

    yield* recalculateBenefitCalculationDetailsSaga({
      type: recalculateBenefitCalculationDetails.type,
      payload: { coverageId, claimId },
    });

    SnacksService.add({
      type: SnackType.Success,
      title: `Updates saved successfully!`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the updates, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    console.error(error);
  }
}

export function* benefitCalculationDetailsWatcher() {
  yield takeLatest([getBenefitCalculationDetails.type], getBenefitCalculationDetailsSaga);
  yield takeLatest([createBenefitCalculationDetails.type], createBenefitCalculationDetailsSaga);
  yield takeLatest([recalculateBenefitCalculationDetails.type], recalculateBenefitCalculationDetailsSaga);
  yield takeLatest([updateBenefitCalculationField.type], updateBenefitCalculationFieldSaga);
  yield takeLatest([overrideBenefitCalculationFields.type], overrideBenefitCalculationFieldsSaga);
}
